import { computed, inject, Injectable, signal } from '@angular/core';
import { Company } from 'src/app/user/model/user';
import { CurrentUserService } from './current-user.service';

const STORAGE_KEY = 'actingCompanyId';

/**
 * Service to manage super admin company context switching.
 *
 * Allows super admins to "act as" another company, affecting which
 * data they see and which company new trips are created for.
 */
@Injectable({
  providedIn: 'root',
})
export class CompanyContextService {
  private currentUserService = inject(CurrentUserService);

  // The company the super admin is currently acting as (null = own company)
  actingCompany = signal<Company | null>(null);

  // Computed: effective company ID for API calls
  effectiveCompanyId = computed(() => {
    const acting = this.actingCompany();
    if (acting?.id) {
      return acting.id;
    }
    return this.currentUserService.currentUser()?.companyId ?? '';
  });

  // Computed: effective company name for display
  effectiveCompanyName = computed(() => {
    const acting = this.actingCompany();
    if (acting?.name) {
      return acting.name;
    }
    return this.currentUserService.currentUser()?.companyName ?? '';
  });

  // Computed: whether currently acting as another company
  isActingAsOtherCompany = computed(() => {
    const acting = this.actingCompany();
    return !!acting && acting.id !== this.currentUserService.ownCompanyId();
  });

  constructor() {
    // Load persisted selection on init
    this.loadPersistedSelection();
  }

  /**
   * Set the company to act as
   */
  setActingCompany(company: Company | null): void {
    this.actingCompany.set(company);
    this.persistSelection(company);
  }

  /**
   * Clear the acting company (return to own company)
   */
  clearActingCompany(): void {
    this.actingCompany.set(null);
    localStorage.removeItem(STORAGE_KEY);
  }

  /**
   * Get the acting company ID for the HTTP header
   * Returns null if not acting as another company
   */
  getActingCompanyIdForHeader(): string | null {
    const acting = this.actingCompany();

    // Only return ID if acting as a different company
    if (acting?.id && acting.id !== this.currentUserService.ownCompanyId()) {
      return acting.id;
    }
    return null;
  }

  private persistSelection(company: Company | null): void {
    if (company?.id) {
      localStorage.setItem(STORAGE_KEY, company.id);
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  }

  private loadPersistedSelection(): void {
    const savedId = localStorage.getItem(STORAGE_KEY);
    if (savedId) {
      // We only save the ID, so we create a minimal Company object
      // The full company data will be loaded when the selector component mounts
      this.actingCompany.set({ id: savedId } as Company);
    }
  }
}
